import { Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { ApiEpUsers } from "@smp/api";
import { UiBox, UiDivider, UiMenu, UiMenuItem } from "@smp/ui";
import { testAppendAttr } from "@smp/utils";
import { IconLogout } from "@tabler/icons-react";
import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";

import { useContextLanguage } from "@/context/language";
import { ELocale } from "@/context/language/language.types";
import { useContextSupportMessenger } from "@/context/support_messenger";
import { useEpUsersMe } from "@/hooks/use_ep_users_me";
import { useContextApp } from "@/screens/app/context/app";
import { getValidLocale } from "@/utils";

import { MenuItemLanguage } from "./menu_item_language";
import { useStyles } from "./menu_user.css";

export const MenuUser = ({
  $anchor,
  marginTop,
  onClose,
}: {
  $anchor: null | HTMLElement;
  marginTop: number;
  onClose: () => void;
}) => {
  const classes = useStyles();

  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useLingui();
  const queryClient = useQueryClient();

  const {
    actions: { supportMessangerUpdate },
  } = useContextSupportMessenger();

  const {
    state: { me },
    actions: { organizationIdChange },
  } = useContextApp();

  const { meUpdatePartial } = useEpUsersMe();

  const { changeLanguage } = useContextLanguage();

  const [isExpanded, setIsExpanded] = useState(false);

  const [languageCodeLoading, setLanguageCodeLoading] = useState<
    string | undefined
  >();

  const languageCodes = useMemo(() => Object.values(ELocale), []);

  const handleLanguageChange = useCallback(
    async (languageCode: string) => {
      setLanguageCodeLoading(languageCode);
      try {
        await meUpdatePartial({ language: languageCode });
        changeLanguage(getValidLocale(languageCode));
        onClose();
        setIsExpanded(false);
      } catch {}
      setLanguageCodeLoading(undefined);
    },
    [changeLanguage, meUpdatePartial, onClose],
  );

  const navigateAdmin = useCallback(() => {
    window.open("/admin/", "_blank", "noopener");
    onClose();
  }, [onClose]);

  const navigateBackoffice = useCallback(() => {
    window.open("/backoffice/", "_blank", "noopener");
    onClose();
  }, [onClose]);

  const navigateGraphql = useCallback(() => {
    window.open("/home/graphql/", "_blank", "noopener");
    onClose();
  }, [onClose]);

  const navigateSwagger = useCallback(() => {
    window.open("/swagger/", "_blank", "noopener");
    onClose();
  }, [onClose]);

  const navigateStorybook = useCallback(() => {
    window.open("/home/storybook/", "_blank", "noopener");
    onClose();
  }, [onClose]);

  const navigateSettings = useCallback(() => {
    navigate("/settings", {
      state: {
        prevPath: location.pathname,
      },
    });
    onClose();
  }, [location.pathname, navigate, onClose]);

  const handleExpandToggle = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const handleLogout = useCallback(async () => {
    // TODO: check how it works on react-query@3
    // RESULT: it has the same bug as react-query@2
    queryClient.clear();
    await ApiEpUsers.common.logout();
    navigate("/auth/signin");
    supportMessangerUpdate({});
    organizationIdChange({ organizationId: null });
  }, [navigate, queryClient, supportMessangerUpdate, organizationIdChange]);

  return useMemo(
    () => (
      <UiMenu
        anchorEl={$anchor}
        anchorOrigin={{
          horizontal: "center",
          vertical: "bottom",
        }}
        onClose={onClose}
        open={Boolean($anchor)}
        style={{ marginTop }}
        transformOrigin={{
          horizontal: "center",
          vertical: "bottom",
        }}
      >
        <MenuItemLanguage
          isExpanded={isExpanded}
          isExpander={true}
          languageCode={i18n.locale}
          onClick={handleExpandToggle}
        />
        {isExpanded &&
          languageCodes
            .filter((languageCode) => languageCode !== i18n.locale)
            .map((languageCode) => (
              <MenuItemLanguage
                key={languageCode}
                isLoading={languageCodeLoading === languageCode}
                languageCode={languageCode}
                onClick={handleLanguageChange}
              />
            ))}
        <UiDivider className={classes.divider} />
        {(me.is_superuser || me.is_staff) && (
          <UiMenuItem onClick={navigateAdmin}>
            <Trans>Administrative Tools</Trans>
          </UiMenuItem>
        )}
        {(me.is_superuser ||
          me.permissions.includes("easypost.backoffice_dashboard")) && (
          <UiMenuItem onClick={navigateBackoffice}>
            <Trans>Back office</Trans>
          </UiMenuItem>
        )}
        {me.is_superuser && (
          <UiMenuItem onClick={navigateGraphql}>GraphQL</UiMenuItem>
        )}
        {me.is_superuser && (
          <UiMenuItem onClick={navigateSwagger}>Swagger</UiMenuItem>
        )}
        {me.is_staff && (
          <UiMenuItem onClick={navigateStorybook}>Storybook</UiMenuItem>
        )}
        <UiMenuItem onClick={navigateSettings}>
          <Trans>Settings</Trans>
        </UiMenuItem>
        <UiDivider className={classes.divider} />
        <UiMenuItem
          onClick={handleLogout}
          style={{ minWidth: 160 }}
          {...testAppendAttr("id", "controls_user.exit")}
        >
          <UiBox display="flex" flexDirection="row" width="100%">
            <UiBox display="flex" alignItems="center" flexGrow={1}>
              <Trans>Exit</Trans>
            </UiBox>
            <UiBox display="flex" alignItems="center" flexGrow={0}>
              <IconLogout />
            </UiBox>
          </UiBox>
        </UiMenuItem>
      </UiMenu>
    ),
    [
      $anchor,
      onClose,
      marginTop,
      isExpanded,
      i18n.locale,
      handleExpandToggle,
      languageCodes,
      classes.divider,
      me.is_superuser,
      me.is_staff,
      me.permissions,
      navigateAdmin,
      navigateBackoffice,
      navigateGraphql,
      navigateSwagger,
      navigateStorybook,
      navigateSettings,
      handleLogout,
      languageCodeLoading,
      handleLanguageChange,
    ],
  );
};
